/**
 * TemplateEditor Component
 *
 * Main template editing interface displaying:
 * - Editable template name
 * - Description (for public templates)
 * - Last saved timestamp
 * - Sections list with drag-and-drop
 */

import React from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { EDITOR_MAX_WIDTH_WITH_PADDING } from '../constants/layout';
import type { SectionWithMeta } from '../types';

import { TemplateSectionsList } from './TemplateSectionsList';

interface TemplateEditorProps {
  /** Template name */
  templateName: string;
  /** Template sections */
  sections: SectionWithMeta[];
  /** Whether template is loading */
  isLoading: boolean;
  /** Whether template can be modified */
  canModify: boolean;
  /** Whether title is being edited */
  isTitleEditing: boolean;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Public description (if public template) */
  publicDescription: string | null;
  /** Whether module selector is open */
  isAddingSections: boolean;
  /** Callback to start editing title */
  onStartEditingTitle: () => void;
  /** Callback when title changes */
  onTitleChange: (title: string) => void;
  /** Callback when title editing finishes (may save asynchronously) */
  onFinishEditingTitle: () => void | Promise<void>;
  /** Callback to reorder sections */
  onReorderSections: (startIndex: number, endIndex: number) => void;
  /** Callback to remove section */
  onRemoveSection: (moduleId: string) => void;
  /** Callback to revert customization */
  onRevertCustomization: (moduleId: string) => void;
  /** Callback to add section at index */
  onAddSectionAt: (index: number) => void;
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  templateName,
  sections,
  isLoading,
  canModify,
  isTitleEditing,
  lastSaved,
  publicDescription,
  isAddingSections,
  onStartEditingTitle,
  onTitleChange,
  onFinishEditingTitle,
  onReorderSections,
  onRemoveSection,
  onRevertCustomization,
  onAddSectionAt,
}) => {

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onFinishEditingTitle();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: EDITOR_MAX_WIDTH_WITH_PADDING,
          mx: 'auto',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Skeleton variant="text" width="60%" height={48} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 4 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={72}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: EDITOR_MAX_WIDTH_WITH_PADDING,
        mx: 'auto',
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Template Header */}
      <Box sx={{ mb: 4 }}>
        {/* Title */}
        <Box sx={{ mb: 2 }}>
          {isTitleEditing ? (
            <TextField
              value={templateName}
              onChange={e => onTitleChange(e.target.value)}
              onBlur={onFinishEditingTitle}
              onKeyDown={handleTitleKeyDown}
              autoFocus
              fullWidth
              variant="standard"
              InputProps={{
                sx: {
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  '& input': {
                    padding: 0,
                  },
                },
                disableUnderline: false,
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                color="text.primary"
                sx={{
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  lineHeight: 1.3,
                }}
              >
                {templateName}
              </Typography>
              {canModify && (
                <Tooltip title="Edit name">
                  <IconButton
                    size="small"
                    onClick={onStartEditingTitle}
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Description (public templates) */}
        {publicDescription && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {publicDescription}
            </Typography>
          </Box>
        )}

        {/* Last Saved Indicator */}
        {lastSaved && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: 'success.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.light',
            }}
          >
            <Typography
              variant="body2"
              fontWeight={500}
              color="success.dark"
            >
              Last saved:{' '}
              {lastSaved.toLocaleString('en-US', { timeZoneName: 'short' })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Sections Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          color="text.primary"
        >
          Sections
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {canModify
            ? 'Drag sections to reorder. Click between sections to add new ones.'
            : 'This is a read-only template. Duplicate to customize.'}
        </Typography>
      </Box>

      {/* Sections List */}
      <TemplateSectionsList
        sections={sections}
        canModify={canModify}
        onReorderSections={onReorderSections}
        onRemoveSection={onRemoveSection}
        onRevertCustomization={onRevertCustomization}
        onAddSectionAt={onAddSectionAt}
        isAddingSections={isAddingSections}
      />
    </Box>
  );
};
